import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { api, ApiClientError } from "../lib/api";
import { useToast } from "../lib/toast";
import type { PartUsed, RepairStatus } from "@sbd-tcic/shared-types";

interface RepairOrderPaymentDto {
  id: string;
  customerName: string;
  equipment: string;
  serviceType: string;
  grcPrice: number;
  laborRate: number;
  laborHours: number;
  deliveryMethod: string;
  freightRate: number;
  status: RepairStatus;
  partsUsed: PartUsed[];
}

const TAX_RATE = 0.0825;
const PAYMENT_METHODS = ["Cash", "Check", "Credit Card", "On Account"];
const money = (n: number) => `$${n.toFixed(2)}`;

export function RepairOrderPayment() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [ro, setRo] = useState<RepairOrderPaymentDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS[0]);
  const [signerName, setSignerName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ orderNumber: string; totalDue: number; fulfillmentStatus: string } | null>(null);

  useEffect(() => {
    if (!id) return;
    void api.get<RepairOrderPaymentDto>(`/repair-orders/${id}`).then((data) => {
      setRo(data);
      setLoading(false);
    });
  }, [id]);

  // Client-side preview using the identical GRC-cap formula the server applies — the server's
  // response after submit remains the authoritative figure; this is a real computation on real
  // fetched data, not a fabricated placeholder (BR-011).
  const preview = useMemo(() => {
    if (!ro) return null;
    const partsListCost = ro.partsUsed.reduce((sum, p) => sum + p.qty * p.listPrice, 0);
    const laborCost = ro.laborRate * ro.laborHours;
    const repairTotalBeforeCap = partsListCost + laborCost;
    const isGrc = ro.serviceType.startsWith("GRC");
    const amountDue = isGrc && ro.grcPrice > 0 ? Math.min(repairTotalBeforeCap, ro.grcPrice) : repairTotalBeforeCap;
    const grcCapDiscount = isGrc && ro.grcPrice > 0 ? Math.max(0, repairTotalBeforeCap - ro.grcPrice) : 0;
    const freightCharge = ro.deliveryMethod === "Ship Out to Customer" ? ro.freightRate : 0;
    const tax = amountDue * TAX_RATE;
    const totalDue = Math.round((amountDue + freightCharge + tax) * 100) / 100;
    return { partsListCost, laborCost, repairTotalBeforeCap, amountDue, grcCapDiscount, freightCharge, tax, totalDue };
  }, [ro]);

  async function submit() {
    if (!id) return;
    setBusy(true);
    try {
      const res = await api.post<{ orderNumber: string; totalDue: number; fulfillmentStatus: string }>(`/repair-orders/${id}/complete-payment`, {
        paymentMethod,
        signed: true,
      });
      setResult(res);
      toast(`Payment collected — order ${res.orderNumber} closed.`, "success");
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to complete payment.", "error");
    } finally {
      setBusy(false);
    }
  }

  if (loading || !ro || !preview) return <div className="text-ink-muted text-sm py-10 text-center">Loading…</div>;

  if (ro.status !== "Complete" && !result) {
    return (
      <div className="card p-6 max-w-lg mx-auto text-center">
        <div className="font-bold mb-2">This repair isn't ready for payment yet</div>
        <p className="text-sm text-ink-muted mb-4">All safety checks must Pass and the repair must be marked Complete first.</p>
        <button className="btn-primary" onClick={() => navigate(`/repair-orders/${ro.id}`)}>
          Back to Work Order
        </button>
      </div>
    );
  }

  if (result) {
    return (
      <div className="card p-8 max-w-lg mx-auto text-center">
        <CheckCircle2 size={40} className="text-status-green mx-auto mb-3.5" />
        <div className="text-lg font-extrabold mb-1">Order {result.orderNumber} Closed</div>
        <div className="text-sm text-ink-muted mb-4">
          Total collected: <span className="font-bold text-ink">{money(result.totalDue)}</span>
          <br />
          Fulfillment status: <span className="font-bold text-ink">{result.fulfillmentStatus}</span>
        </div>
        <div className="flex gap-2.5 justify-center">
          <button className="btn-outline" onClick={() => navigate("/orders")}>
            View Order History
          </button>
          <button className="btn-primary" onClick={() => navigate("/repair-orders/new")}>
            New Repair Order
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto flex flex-col gap-5">
      <div className="card p-5">
        <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted mb-3">{ro.id} — {ro.customerName}</h2>
        <div className="text-sm text-ink-muted mb-3.5">{ro.equipment} · {ro.serviceType}</div>
        <div className="flex flex-col gap-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-ink-muted">Parts (list)</span>
            <span className="font-semibold">{money(preview.partsListCost)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-muted">Labor</span>
            <span className="font-semibold">{money(preview.laborCost)}</span>
          </div>
          {preview.grcCapDiscount > 0 && (
            <div className="flex justify-between text-status-green">
              <span>GRC Cap Discount</span>
              <span className="font-semibold">−{money(preview.grcCapDiscount)}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-ink-muted">Amount Due (before freight/tax)</span>
            <span className="font-semibold">{money(preview.amountDue)}</span>
          </div>
          {preview.freightCharge > 0 && (
            <div className="flex justify-between">
              <span className="text-ink-muted">Freight</span>
              <span className="font-semibold">{money(preview.freightCharge)}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-ink-muted">Tax</span>
            <span className="font-semibold">{money(preview.tax)}</span>
          </div>
          <div className="flex justify-between text-base border-t border-border pt-2 mt-1">
            <span className="font-bold">Total Due</span>
            <span className="font-extrabold">{money(preview.totalDue)}</span>
          </div>
        </div>
      </div>

      <div className="card p-5">
        <label className="field-label">Payment Method</label>
        <select className="input mb-4" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
          {PAYMENT_METHODS.map((m) => (
            <option key={m}>{m}</option>
          ))}
        </select>

        <label className="field-label">Customer Signature</label>
        <div className="border-2 border-dashed border-border rounded-md h-24 flex items-center justify-center text-ink-muted text-xs mb-2">
          Signature capture area
        </div>
        <input className="input mb-4" placeholder="Type full name to authorize" value={signerName} onChange={(e) => setSignerName(e.target.value)} />

        <button className="btn-primary w-full justify-center" disabled={!signerName.trim() || busy} onClick={() => void submit()}>
          {busy ? "Processing…" : `Collect Payment & Close — ${money(preview.totalDue)}`}
        </button>
      </div>
    </div>
  );
}
